import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box, Button, Card, CardHeader, CardContent, TextField, Typography,
  Grid, CircularProgress,
} from "@mui/material";
import { ArrowBack, Business, Save } from "@mui/icons-material";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const schema = yup.object({
  name: yup.string().required("Account name is required"),
  industry: yup.string(),
  employee_count: yup.number().nullable().typeError("Must be a number"),
  website: yup.string().url("Enter a valid URL").nullable().transform((v) => v || null),
  phone: yup.string(),
  email: yup.string().email("Enter a valid email"),
  address_street: yup.string(),
  address_city: yup.string(),
  address_state: yup.string(),
  address_postal_code: yup.string(),
  address_country: yup.string(),
  linkedin: yup.string().url("Enter a valid URL").nullable().transform((v) => v || null),
  notes: yup.string(),
});

export default function CompanyFormPage({
  companyId,
  drawerMode = false,
  hideHeader = false,
  onClose,
  onSaved,
} = {}) {
  const history = useHistory();
  const params = useParams();
  const id = companyId ?? params.id;
  const isEdit = Boolean(id);
  const { domain } = useSelector((s) => s.tokenReducer || {});

  const [initialLoading, setInitialLoading] = useState(isEdit);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(schema), defaultValues: { name: "", industry: "", employee_count: null, website: "", phone: "", email: "", address_street: "", address_city: "", address_state: "", address_postal_code: "", address_country: "", linkedin: "", notes: "" } });

  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/companies/${id}/`);
        const d = res.responseDetails;
        reset({
          name: d.name || "",
          industry: d.industry || "",
          employee_count: d.employee_count ?? null,
          website: d.website || "",
          phone: d.phone || "",
          email: d.email || "",
          address_street: d.address_street || "",
          address_city: d.address_city || "",
          address_state: d.address_state || "",
          address_postal_code: d.address_postal_code || "",
          address_country: d.address_country || "",
          linkedin: d.linkedin || "",
          notes: d.notes || "",
        });
      } catch {
        toast.error("Failed to load account");
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, [id, domain, isEdit, reset]);

  const onSubmit = async (data) => {
    const payload = { ...data, employee_count: data.employee_count || null };
    try {
      let response;
      if (isEdit) {
        response = await customAPI(`${domain}crm/companies/${id}/`, "PATCH", payload);
        toast.success("Account updated");
      } else {
        response = await customAPI(`${domain}crm/companies/`, "POST", payload);
        toast.success("Account created");
      }
      if (onSaved) {
        await onSaved(response?.responseDetails);
      } else {
        history.push("/admin/crm/companies");
      }
    } catch {
      toast.error("Save failed");
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      history.push("/admin/crm/companies");
    }
  };

  if (initialLoading) {
    return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;
  }

  const Field = ({ name, label, required, type = "text", multiline, rows }) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          label={label}
          required={required}
          fullWidth
          size="small"
          type={type}
          multiline={multiline}
          rows={rows}
          error={!!errors[name]}
          helperText={errors[name]?.message}
          value={field.value ?? ""}
        />
      )}
    />
  );

  return (
    <Box p={drawerMode ? 0 : 3} maxWidth={drawerMode ? "none" : 860} mx={drawerMode ? 0 : "auto"}>
      {!hideHeader && (
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <Button startIcon={<ArrowBack />} onClick={handleClose} size="small">Back</Button>
          <Business color="primary" />
          <Typography variant="h5" fontWeight={700}>{isEdit ? "Edit Account" : "New Account"}</Typography>
        </Box>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title="Basic Info" titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }} />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><Field name="name" label="Account Name" required /></Grid>
              <Grid item xs={12} sm={6}><Field name="industry" label="Sector" /></Grid>
              <Grid item xs={12} sm={6}><Field name="employee_count" label="Employee Count" type="number" /></Grid>
              <Grid item xs={12} sm={6}><Field name="website" label="Website URL" /></Grid>
              <Grid item xs={12} sm={6}><Field name="phone" label="Phone" /></Grid>
              <Grid item xs={12} sm={6}><Field name="email" label="Email" /></Grid>
              <Grid item xs={12} sm={6}><Field name="linkedin" label="LinkedIn URL" /></Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title="Address" titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }} />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}><Field name="address_street" label="Street" /></Grid>
              <Grid item xs={12} sm={6}><Field name="address_city" label="City" /></Grid>
              <Grid item xs={12} sm={6}><Field name="address_state" label="State / Province" /></Grid>
              <Grid item xs={12} sm={6}><Field name="address_postal_code" label="Postal Code" /></Grid>
              <Grid item xs={12} sm={6}><Field name="address_country" label="Country" /></Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardHeader title="Notes" titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }} />
          <CardContent>
            <Field name="notes" label="Notes" multiline rows={3} />
          </CardContent>
        </Card>

        <Box display="flex" gap={2}>
          <Button variant="contained" type="submit" startIcon={<Save />} disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={18} /> : isEdit ? "Save Changes" : "Create Account"}
          </Button>
          <Button variant="outlined" onClick={handleClose}>Cancel</Button>
        </Box>
      </form>
    </Box>
  );
}
